import { ICellEditorAngularComp } from 'ag-grid-angular';
import { AfterViewInit, Component, Inject, OnDestroy, ViewChild, ViewContainerRef } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { digger } from '@cbre/digger';
import { formatNumber } from '@miq/utils';
import { toFloat, toInt } from '@shared/numerics/parse-number';
import { isMasked } from '@shared/utils/helpers/sanitizers';
import { MiqAgColumn } from '../ag-table-grid-options';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ICellRendererParams } from 'ag-grid-community';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

@UntilDestroy()
@Component({
  selector: 'miq-ag-numeric',
  templateUrl: './ag-numeric.component.html',
  styleUrls: ['./ag-numeric.component.scss'],
  standalone: false,
})
export class AgNumericComponent implements ICellEditorAngularComp, AfterViewInit, OnDestroy {
  constructor(@Inject(CbreLoggerToken) private readonly logger: CbreLogger) {}
  public params: any;
  currentValue: any;

  column: MiqAgColumn & { field: string };

  fc: UntypedFormControl;
  isMaskedValue = false;
  disabled = false;
  ignoreFormatting = false;
  allowedKeyCodes: number[] = [];
  restrictedKeyCodes: number[] = [];
  maxAllowedChars = null; // column.maxLength

  private focusWhenCreate = false;
  private componentTouched = false;

  private decimalsCount = 0;
  readonly #defaultMaxAllowedChars = 99;

  @ViewChild('input', { read: ViewContainerRef, static: false }) public input;

  /**
   * Required by ICellEditorAngularComp
   */
  agInit(params: any) {
    this.params = params;
    const paramsObject =
      typeof params?.cellEditorParams === 'function' ? params?.cellEditorParams(params) : params?.cellEditorParams;

    this.column = params.colDef;

    const path = this.column.getReadPath != null ? this.column.getReadPath() : this.column.field;
    this.currentValue = digger(params?.data ?? {}, path);
    if (!this.currentValue || params.useValGetForInput) {
      this.currentValue = params.value;
    }
    this.focusWhenCreate = !!params.focusWhenCreate || paramsObject?.focusWhenCreate;
    this.ignoreFormatting = params.ignoreFormatting;

    if (params.allowNegativeNumbers || paramsObject?.allowNegativeNumbers) {
      this.allowedKeyCodes = [...this.allowedKeyCodes, /*-*/ 189, /*-*/ 109];
    }

    if (params.isIntegerValue || paramsObject?.isIntegerValue) {
      this.restrictedKeyCodes = [...this.restrictedKeyCodes, /*.*/ 190];
    }
    this.isMaskedValue = isMasked(this.currentValue);
    const initialOptions = { value: this.currentValue, disabled: this.isMaskedValue };
    this.fc = new UntypedFormControl(initialOptions);

    this.fc.setValue(this.formatValueToDisplay(this.currentValue, this.ignoreFormatting));
    this.fc.valueChanges.pipe(untilDestroyed(this)).subscribe(() => this.parseValue());

    // Set a big default in case is not configured
    this.maxAllowedChars = params.colDef.maxLength ?? this.#defaultMaxAllowedChars;
  }

  /**
   * Required by ICellEditorAngularComp
   */
  ngAfterViewInit() {
    if (this.focusWhenCreate) {
      setTimeout(() => this.input?.element?.nativeElement?.focus());
    }
  }

  ngOnDestroy() {
    this.input = null;
  }

  /**
   * Required by ICellEditorAngularComp
   */
  getValue() {
    return this.currentValue;
  }

  formatValueToDisplay(value, ignoreFormatting = false) {
    if (isMasked(value) || ignoreFormatting) {
      return value;
    }
    if (this.isValidToFormat(value)) {
      return `${this.applyFormat(value)}`;
    }
    return this.isValidToFormat(value) ? value : '';
  }

  getColumnKey = (agGridParams: ICellRendererParams, field: string) => {
    const colKey = agGridParams.colDef?.colId || field;
    if (colKey == null) {
      this.logger.error({ eventName: 'Column key is not defined for the column', columnDef: agGridParams.colDef });
      return null;
    }
    if (colKey != field) {
      this.logger.warn(`MIQ: Column key "${colKey}" is different from the field "${field}".`);
    }
    return colKey;
  };

  public parseValue(): void {
    this.componentTouched = true;

    if (this.isValidToFormat(this.fc.value)) {
      const columnKey = this.getColumnKey(this.params, this.column.field);
      if (columnKey == null) {
        this.logger.error({ eventName: 'Edit blocked due to column configuration', column: this.column });
        return;
      }
      const [, decimals] = `${this.fc.value}`.split('.');
      this.decimalsCount = decimals != null ? decimals.length : 0;
      const value =
        this.decimalsCount > 0 || !this.params.isIntegerValue ? toFloat(this.fc.value) : toInt(this.fc.value);
      this.currentValue = value;
      this.params.node.setDataValue(columnKey, this.currentValue);
      return;
    }
    this.currentValue = null;
    this.decimalsCount = null;
  }

  public isCancelAfterEnd(): boolean {
    return !this.componentTouched;
  }

  private applyFormat(val: number | string): string | number {
    const digitInfo = this.decimalsCount ? `0.${this.decimalsCount}` : '0.0';
    val = this.decimalsCount > 0 ? toFloat(val) : toInt(val);
    // Keep compatibility with old behavior when the maxAllowedChars is not set
    return this.maxAllowedChars === this.#defaultMaxAllowedChars ? formatNumber(val, 'en', digitInfo) : val;
  }

  private isValidToFormat(value): boolean {
    return value != null && !Number.isNaN(Number.parseFloat(value));
  }
}
